import React, { useState } from "react";
import { Alert, Button, Card, Spinner, Modal } from "react-bootstrap";
import { useSubscription } from "../hooks/useSubscription";
import SubscriptionStatus from "./SubscriptionStatus";

/**
 * SubscriptionGuard Component
 * Issue #9: Frontend State Management
 *
 * Protects content based on subscription status
 * Shows appropriate messages and actions when access is denied
 */
const SubscriptionGuard = ({
  children,
  feature = "general",
  fallback = null,
  showTrialPrompt = true,
  showSubscriptionStatus = true,
  requireActive = false,
  customMessage = null,
  className = "",
}) => {
  const {
    hasAccess,
    isAuthenticated,
    isAdmin,
    loading,
    error,
    subscription,
    isInTrial,
    getTrialDaysRemaining,
    canActivateTrial,
    activateTrial,
  } = useSubscription();

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Loading state
  if (loading) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">
            Loading subscription status...
          </span>
        </Spinner>
        <div className="mt-2 text-muted">Checking subscription...</div>
      </div>
    );
  }

  // Not authenticated - don't show content
  if (!isAuthenticated) {
    return (
      fallback || (
        <Alert variant="warning" className={className}>
          <i className="fas fa-sign-in-alt me-2"></i>
          Please log in to access this feature.
        </Alert>
      )
    );
  }

  // Admin users always have access
  if (isAdmin) {
    return children;
  }

  // Error state
  if (error) {
    return (
      <Alert variant="danger" className={className}>
        <i className="fas fa-exclamation-triangle me-2"></i>
        Unable to verify subscription status. {error}
      </Alert>
    );
  }

  // Check subscription requirements
  const hasValidSubscription =
    subscription &&
    (subscription.status === "active" ||
      subscription.is_in_trial ||
      subscription.in_grace_period);

  // If requireActive is true, only allow active subscriptions (not trial)
  if (requireActive && (!subscription || subscription.status !== "active")) {
    return (
      <div className={className}>
        <Alert variant="warning">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-1">
                <i className="fas fa-lock me-2"></i>
                Premium Feature
              </h6>
              <small>
                {customMessage ||
                  "This feature requires an active subscription."}
              </small>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowUpgradeModal(true)}
            >
              Upgrade Now
            </Button>
          </div>
        </Alert>

        {showSubscriptionStatus && (
          <SubscriptionStatus showDetails={true} className="mt-3" />
        )}

        <UpgradeModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
        />
      </div>
    );
  }

  // Check if user has general access
  if (hasAccess || hasValidSubscription) {
    return children;
  }

  // No access - show appropriate message and actions
  const handleActivateTrial = async () => {
    setActionLoading(true);
    try {
      const result = await activateTrial();
      if (!result.success) {
        console.error("Trial activation failed:", result.message);
      }
    } catch (error) {
      console.error("Trial activation error:", error);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className={className}>
      <Card className="border-warning">
        <Card.Body className="text-center">
          <div className="mb-3">
            <i
              className="fas fa-lock text-warning"
              style={{ fontSize: "3rem" }}
            ></i>
          </div>

          <h5>Access Required</h5>
          <p className="text-muted">
            {customMessage ||
              "This feature requires an active subscription or trial."}
          </p>

          {/* Show trial option if available */}
          {showTrialPrompt && canActivateTrial() && (
            <div className="mb-3">
              <Button
                variant="success"
                onClick={handleActivateTrial}
                disabled={actionLoading}
                className="me-2"
              >
                {actionLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Starting Trial...
                  </>
                ) : (
                  <>
                    <i className="fas fa-gift me-2"></i>
                    Start 3-Day Free Trial
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Show upgrade option */}
          <div className="mb-3">
            <Button variant="primary" onClick={() => setShowUpgradeModal(true)}>
              <i className="fas fa-credit-card me-2"></i>
              View Subscription Plans
            </Button>
          </div>

          {/* Trial expiration warning */}
          {isInTrial() && getTrialDaysRemaining() <= 1 && (
            <Alert variant="warning" className="mt-3">
              <small>
                <i className="fas fa-clock me-1"></i>
                Your trial expires in {getTrialDaysRemaining()} day
                {getTrialDaysRemaining() !== 1 ? "s" : ""}
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Show current subscription status */}
      {showSubscriptionStatus && (
        <div className="mt-3">
          <SubscriptionStatus showDetails={true} />
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        show={showUpgradeModal}
        onHide={() => setShowUpgradeModal(false)}
      />

      {/* Custom fallback content */}
      {fallback && <div className="mt-3">{fallback}</div>}
    </div>
  );
};

/**
 * Simple Upgrade Modal Component
 */
const UpgradeModal = ({ show, onHide }) => {
  const { plans, plansLoading } = useSubscription();

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Subscription Plans</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {plansLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="mt-2">Loading plans...</div>
          </div>
        ) : (
          <div className="row">
            {plans.map((plan) => (
              <div key={plan.id} className="col-md-6 mb-3">
                <Card className="h-100">
                  <Card.Body className="text-center">
                    <h5>{plan.name}</h5>
                    <div className="display-6 text-primary">
                      ${plan.price}
                      <small className="text-muted">/{plan.interval}</small>
                    </div>
                    <ul className="list-unstyled mt-3">
                      {plan.features?.map((feature, index) => (
                        <li key={index} className="mb-1">
                          <i className="fas fa-check text-success me-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                  <Card.Footer className="text-center">
                    <Button variant="primary" size="sm">
                      Choose Plan
                    </Button>
                  </Card.Footer>
                </Card>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubscriptionGuard;
